/**
 * Editor text boxes: resizable Fabric Rect + inner Textbox (canvas siblings).
 * The Rect uses normal Fabric resize; the Textbox holds text / fieldKey / layout.
 */

import {
  snapshotTextFieldConfig,
  reflowTextboxCopy,
  reflowTextboxWidthPreserveHeight,
  ensureTextObjectVisible,
} from './textLayoutEngine';

/** @deprecated Groups use this flag; pairs use frame/label flags below. */
export const TEXT_BOX_GROUP_FLAG = '_textBoxGroup';

export const TEXT_BOX_PAIR_ID = '_textBoxPairId';
export const TEXT_BOX_FRAME_FLAG = '_textBoxFrame';
export const TEXT_BOX_LABEL_FLAG = '_textBoxLabel';

const MIN_BOX = 24;
const PAD = 2;

let pairIdCounter = 0;
function nextPairId() {
  pairIdCounter += 1;
  return `tbp_${Date.now()}_${pairIdCounter}`;
}

export function isTextBoxFrame(obj) {
  if (!obj || obj.type !== 'rect') return false;
  return !!(obj[TEXT_BOX_FRAME_FLAG] || obj.get?.(TEXT_BOX_FRAME_FLAG));
}

export function isTextBoxLabel(obj) {
  if (!obj || obj.type !== 'textbox') return false;
  return !!(obj[TEXT_BOX_LABEL_FLAG] || obj.get?.(TEXT_BOX_LABEL_FLAG));
}

export function getTextBoxPairId(obj) {
  if (!obj) return null;
  return obj[TEXT_BOX_PAIR_ID] || (typeof obj.get === 'function' ? obj.get(TEXT_BOX_PAIR_ID) : null) || null;
}

function findPairObject(canvas, pairId, kind) {
  if (!canvas?.getObjects || !pairId) return null;
  return (
    canvas.getObjects().find((o) => getTextBoxPairId(o) === pairId && (kind === 'frame' ? isTextBoxFrame(o) : isTextBoxLabel(o))) ||
    null
  );
}

/** Legacy group-based box (templates saved before paired rects). */
export function isTextBoxGroup(obj) {
  if (!obj || obj.type !== 'group') return false;
  return !!(obj[TEXT_BOX_GROUP_FLAG] || obj.get?.(TEXT_BOX_GROUP_FLAG));
}

function isShapeCaptionGroup(obj) {
  return !!(obj?._shapeLabelGroup || obj?.get?.('_shapeLabelGroup'));
}

export function getTextBoxGroupLabel(group) {
  if (!isTextBoxGroup(group)) return null;
  const objs = group.getObjects?.() || [];
  return objs.find((c) => c.type === 'textbox' || c.type === 'i-text' || c.type === 'text') || null;
}

export function resolveTextBoxFrame(obj) {
  if (!obj) return null;
  if (isTextBoxFrame(obj)) return obj;
  const canvas = obj.canvas;
  const pairId = getTextBoxPairId(obj);
  if (pairId && canvas) return findPairObject(canvas, pairId, 'frame');
  if (isTextBoxGroup(obj)) return null;
  if (obj.group && isTextBoxGroup(obj.group)) return null;
  return null;
}

export function resolveTextBoxLabel(obj) {
  if (!obj) return null;
  if (isTextBoxLabel(obj)) return obj;
  const canvas = obj.canvas;
  const pairId = getTextBoxPairId(obj);
  if (pairId && canvas) return findPairObject(canvas, pairId, 'label');
  if (isTextBoxGroup(obj)) return getTextBoxGroupLabel(obj);
  if (obj.group && isTextBoxGroup(obj.group)) return obj.type === 'textbox' ? obj : null;
  return null;
}

export function isEditorTextBoxTarget(obj) {
  if (!obj) return false;
  if (isTextBoxFrame(obj) || isTextBoxLabel(obj)) return true;
  if (isTextBoxGroup(obj)) return true;
  if (obj.type === 'textbox' && !obj.group) return true;
  return false;
}

export function resolveEditorTextBoxLabel(obj) {
  const label = resolveTextBoxLabel(obj);
  if (label) return label;
  if (obj?.type === 'textbox' && !obj.group && !isShapeCaptionGroup(obj.group)) return obj;
  return null;
}

export function getEditorTextBoxResizeTarget(obj) {
  const frame = resolveTextBoxFrame(obj);
  if (frame) return frame;
  if (isTextBoxGroup(obj)) return obj;
  if (obj?.group && isTextBoxGroup(obj.group)) return obj.group;
  if (obj?.type === 'textbox' && !obj.group) return obj;
  return null;
}

/** Position inner textbox inside the frame bounding box. */
export function syncTextBoxPairGeometry(frame, label) {
  if (!frame || !label) return;
  try {
    frame.setCoords?.();
    const br = frame.getBoundingRect(true);
    const w = Math.max(MIN_BOX, br.width - PAD * 2);
    const h = Math.max(MIN_BOX, br.height - PAD * 2);
    label.set({
      left: br.left + PAD,
      top: br.top + PAD,
      width: w,
      height: h,
      scaleX: 1,
      scaleY: 1,
      originX: 'left',
      originY: 'top',
      angle: frame.angle || 0,
    });
    label.setCoords?.();
  } catch {
    /* ignore */
  }
}

export function beginTextBoxFrameResizeSession(frame) {
  if (!isTextBoxFrame(frame) || frame.__tbFrameResizeSession) return;
  const w = Math.max(MIN_BOX, (frame.width || 100) * Math.abs(frame.scaleX || 1));
  const h = Math.max(MIN_BOX, (frame.height || 40) * Math.abs(frame.scaleY || 1));
  frame.__tbFrameResizeSession = { width: w, height: h };
}

export function clearTextBoxFrameResizeSession(frame) {
  if (frame) delete frame.__tbFrameResizeSession;
}

function scaleFactors(scaleX, scaleY, corner) {
  const sx = Math.abs(scaleX || 1);
  const sy = Math.abs(scaleY || 1);
  if (corner && /^(tl|tr|bl|br)$/.test(String(corner))) {
    const uni = Math.max(sx, sy);
    return { sx: uni, sy: uni };
  }
  return { sx, sy };
}

export function resizeTextBoxFrameFromTransform(canvas, frame, options = {}) {
  if (!isTextBoxFrame(frame) || !canvas) return;
  const sx0 = Math.abs(frame.scaleX || 1);
  const sy0 = Math.abs(frame.scaleY || 1);
  if (Math.abs(sx0 - 1) < 0.002 && Math.abs(sy0 - 1) < 0.002) return;

  beginTextBoxFrameResizeSession(frame);
  const session = frame.__tbFrameResizeSession;
  const corner = options.corner || frame.__corner;
  const { sx, sy } = scaleFactors(sx0, sy0, corner);
  const newW = Math.max(MIN_BOX, session.width * sx);
  const newH = Math.max(MIN_BOX, session.height * sy);

  frame.set({
    width: newW,
    height: newH,
    scaleX: 1,
    scaleY: 1,
    lockScalingY: false,
    lockScalingX: false,
    lockUniScaling: false,
  });
  frame.setCoords?.();

  const label = resolveTextBoxLabel(frame);
  if (label) {
    syncTextBoxPairGeometry(frame, label);
    if (!options.live) reflowTextboxWidthPreserveHeight(label);
  }
  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

export function commitTextBoxFrameResize(canvas, frame) {
  if (!isTextBoxFrame(frame) || !canvas) return;
  clearTextBoxFrameResizeSession(frame);
  const label = resolveTextBoxLabel(frame);
  if (label) {
    syncTextBoxPairGeometry(frame, label);
    reflowTextboxCopy(canvas, label);
    try {
      const fc = snapshotTextFieldConfig(label);
      frame.fieldConfig = fc;
      label.fieldConfig = fc;
      if (frame.fieldKey) {
        label.fieldKey = frame.fieldKey;
        label.set?.('fieldKey', frame.fieldKey);
      }
    } catch {
      /* ignore */
    }
    ensureTextObjectVisible(label);
  }
  frame.setCoords?.();
  canvas.requestRenderAll?.() || canvas.renderAll?.();
}

/** @deprecated Use resizeTextBoxFrameFromTransform */
export function resizeTextBoxGroupFromTransform(canvas, group, options = {}) {
  if (!isTextBoxGroup(group)) return resizeTextBoxFrameFromTransform(canvas, group, options);
}

/** @deprecated */
export function commitTextBoxGroupResize(canvas, group) {
  if (!isTextBoxGroup(group)) return commitTextBoxFrameResize(canvas, group);
}

/** @deprecated */
export function beginTextBoxGroupResizeSession(group) {
  beginTextBoxFrameResizeSession(group);
}

/** @deprecated */
export function clearTextBoxGroupResizeSession(group) {
  clearTextBoxFrameResizeSession(group);
}

export function createResizableTextbox(fabric, options = {}) {
  const {
    text = '',
    left = 0,
    top = 0,
    width = 320,
    height = 120,
    originX = 'center',
    originY = 'center',
    fontSize = 56,
    fontFamily = 'Arial',
    fontWeight = 'normal',
    fontStyle = 'normal',
    fill = '#111827',
    textAlign = 'center',
    fieldKey,
    fieldConfig,
    angle,
  } = options;

  const pairId = nextPairId();
  const boxW = Math.max(MIN_BOX, width);
  const boxH = Math.max(MIN_BOX, height);

  const frame = new fabric.Rect({
    left,
    top,
    originX,
    originY,
    width: boxW,
    height: boxH,
    fill: 'transparent',
    stroke: 'rgba(148, 163, 184, 0.55)',
    strokeWidth: 1,
    lockScalingY: false,
    lockScalingX: false,
    lockUniScaling: false,
  });
  frame[TEXT_BOX_FRAME_FLAG] = true;
  frame[TEXT_BOX_PAIR_ID] = pairId;
  frame.set?.(TEXT_BOX_FRAME_FLAG, true);
  frame.set?.(TEXT_BOX_PAIR_ID, pairId);

  const label = new fabric.Textbox(text, {
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
    fill,
    textAlign,
    editable: true,
    splitByGrapheme: false,
    hasControls: false,
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    selectable: false,
    evented: false,
    dynamicMinHeight: false,
  });
  label[TEXT_BOX_LABEL_FLAG] = true;
  label[TEXT_BOX_PAIR_ID] = pairId;
  label.set?.(TEXT_BOX_LABEL_FLAG, true);
  label.set?.(TEXT_BOX_PAIR_ID, pairId);

  if (fieldKey) {
    frame.fieldKey = fieldKey;
    label.fieldKey = fieldKey;
  }
  label.fieldConfig = fieldConfig || snapshotTextFieldConfig(label);
  frame.fieldConfig = label.fieldConfig;
  if (angle) {
    frame.set({ angle });
    label.set({ angle });
  }

  syncTextBoxPairGeometry(frame, label);
  return { frame, label, pairId };
}

export function addResizableTextboxToCanvas(canvas, fabric, options = {}) {
  const { frame, label } = createResizableTextbox(fabric, options);
  if (!canvas) return null;
  canvas.add(frame);
  canvas.add(label);
  label.moveTo?.(canvas.getObjects().length - 1);
  return frame;
}

function explodeTextBoxGroupToPair(canvas, fabric, group) {
  const label = getTextBoxGroupLabel(group);
  if (!label) return null;
  try {
    group.setCoords?.();
    const br = group.getBoundingRect(true);
    const props = {
      text: label.text,
      left: br.left + br.width / 2,
      top: br.top + br.height / 2,
      width: br.width,
      height: br.height,
      originX: 'center',
      originY: 'center',
      fontSize: label.fontSize,
      fontFamily: label.fontFamily,
      fontWeight: label.fontWeight,
      fontStyle: label.fontStyle,
      fill: label.fill,
      textAlign: label.textAlign,
      fieldKey: group.fieldKey || label.fieldKey,
      fieldConfig: group.fieldConfig || label.fieldConfig,
      angle: group.angle,
    };
    const idx = canvas.getObjects().indexOf(group);
    const { frame, label: newLabel } = createResizableTextbox(fabric, props);
    const active = canvas.getActiveObject?.();
    canvas.remove(group);
    if (idx >= 0 && typeof canvas.insertAt === 'function') {
      canvas.insertAt(frame, idx, false);
      canvas.insertAt(newLabel, idx + 1, false);
    } else {
      canvas.add(frame);
      canvas.add(newLabel);
    }
    if (active === group) canvas.setActiveObject(frame);
    return frame;
  } catch {
    return null;
  }
}

export function migrateEditorTextboxesToResizableGroups(canvas, fabric) {
  if (!canvas?.getObjects || !fabric) return;

  const objects = [...canvas.getObjects()];
  let changed = false;

  objects.forEach((obj) => {
    if (isTextBoxGroup(obj)) {
      if (explodeTextBoxGroupToPair(canvas, fabric, obj)) changed = true;
      return;
    }
    if (obj.type === 'textbox' && !obj.group && !isTextBoxLabel(obj)) {
      const idx = canvas.getObjects().indexOf(obj);
      const props = {
        text: obj.text,
        left: obj.left,
        top: obj.top,
        width: Math.max(MIN_BOX, (obj.width || 200) * Math.abs(obj.scaleX || 1)),
        height: Math.max(MIN_BOX, (obj.height || 80) * Math.abs(obj.scaleY || 1)),
        originX: obj.originX || 'center',
        originY: obj.originY || 'center',
        fontSize: obj.fontSize,
        fontFamily: obj.fontFamily,
        fontWeight: obj.fontWeight,
        fontStyle: obj.fontStyle,
        fill: obj.fill,
        textAlign: obj.textAlign,
        fieldKey: obj.fieldKey,
        fieldConfig: obj.fieldConfig,
        angle: obj.angle,
      };
      const { frame, label } = createResizableTextbox(fabric, props);
      const active = canvas.getActiveObject?.();
      canvas.remove(obj);
      if (idx >= 0 && typeof canvas.insertAt === 'function') {
        canvas.insertAt(frame, idx, false);
        canvas.insertAt(label, idx + 1, false);
      } else {
        canvas.add(frame);
        canvas.add(label);
      }
      if (active === obj) canvas.setActiveObject(frame);
      changed = true;
    }
  });

  if (changed) canvas.renderAll?.();
}

export function restoreAllTextBoxGroupsOnCanvas(canvas) {
  if (!canvas) return;
  canvas.getObjects?.().forEach((obj) => {
    if (!isTextBoxFrame(obj)) return;
    const label = resolveTextBoxLabel(obj);
    if (label) {
      label.set?.({
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        selectable: false,
        evented: false,
      });
      syncTextBoxPairGeometry(obj, label);
    }
  });
  canvas.renderAll?.();
}

/** Select the resizable frame when the inner label was hit. */
export function promoteTextBoxFrameSelection(canvas, target) {
  if (!canvas || !target) return target;
  if (target.isEditing) return target;
  const frame = resolveTextBoxFrame(target);
  if (frame && frame !== target) {
    canvas.setActiveObject(frame);
    frame.setCoords?.();
    return frame;
  }
  return target;
}

export function enterTextBoxGroupEditing(canvas, target) {
  const label = resolveEditorTextBoxLabel(target);
  if (!label || !canvas) return;
  label.set?.({ selectable: true, evented: true });
  canvas.setActiveObject(label);
  if (typeof label.enterEditing === 'function') {
    label.enterEditing();
    if (typeof label.selectAll === 'function') label.selectAll();
  }
  canvas.requestRenderAll?.();
}

export function syncTextBoxGroupAfterTextEdit(canvas, target) {
  const label = resolveTextBoxLabel(target);
  const frame = label ? resolveTextBoxFrame(label) : null;
  if (!label || !frame || !canvas) return;
  label.set?.({ selectable: false, evented: false });
  syncTextBoxPairGeometry(frame, label);
  commitTextBoxFrameResize(canvas, frame);
}
